import React, { Fragment, memo, useEffect, useMemo, useState } from 'react'
import { InputAdornment, Modal, TextField, Typography } from '@mui/material'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { DataRow } from 'src/components/shared/DataRow'
import { Button } from 'src/components/shared/Button'
import { colors } from 'src/theme'
import { showToast } from 'src/utils/toast'
import { captureSentryError } from 'src/utils/sentry'
import { WarningContainer } from '../../WarningContainer'

export interface ExportDataModalColumn {
  field: string
  headerName: string
}

export interface ExportDataModalFilter {
  label: string
  value: string
}

interface Props {
  isOpen: boolean
  onClose(): void
  entityNamePlural: string
  columns: Array<ExportDataModalColumn>
  filters: Array<ExportDataModalFilter>
  defaultCount: number
  maxCount: number
  onExport(count: number): Promise<void>
}

const ExportDataModalComponent = ({
  isOpen,
  onClose,
  entityNamePlural,
  columns,
  filters,
  defaultCount,
  maxCount,
  onExport,
}: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const [count, setCount] = useState<number>(defaultCount)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  // Reset only when the modal opens — a background count refresh must not overwrite the user's input.
  useEffect(() => {
    if (isOpen) {
      setCount(defaultCount)
    }
  }, [isOpen])

  const hasFilters = filters.length > 0

  const handleChangeCount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/[^0-9]/g, '')
    const value = digitsOnly ? Number(digitsOnly) : 0

    setCount(Math.min(value, maxCount))
  }

  const handleClickMax = () => {
    setCount(maxCount)
  }

  const handleKeyDownMax = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (isSubmitting) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClickMax()
    }
  }

  const handleClose = () => {
    if (isSubmitting) {
      return
    }

    onClose()
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      await onExport(count)

      showToast('Export completed successfully. Your file will start downloading shortly.', {
        variant: 'success',
      })
    } catch (error: unknown) {
      captureSentryError(error, {
        message: '[ExportDataModal.handleSubmit] export failed',
      })

      showToast('Export failed. Please try again.', { variant: 'error' })
    } finally {
      setIsSubmitting(false)

      onClose()
    }
  }

  return (
    <Modal
      container={container}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="export-data-modal-title"
      css={tw`flex justify-center items-center backdrop-blur`}
    >
      <div
        data-testid="export-data-modal"
        css={tw`bg-white rounded-[8px] w-[340px] overflow-hidden`}
      >
        <ModalHeader
          title="Export Data"
          titleId="export-data-modal-title"
          showCloseButton={false}
        />
        <div css={tw`p-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto`}>
          <div>
            <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`mb-2`}>
              Selected columns:
            </Typography>
            <Typography variant="body2" color={colors.GRAY_BLACK}>
              {columns.map((column, index) => {
                const isLast = index === columns.length - 1

                return (
                  <Fragment key={column.field}>
                    <span css={tw`whitespace-nowrap`}>
                      {column.headerName}
                      {!isLast && (
                        <span css={{ color: colors.SHADES_GREY_300 }}>&nbsp;&nbsp;•</span>
                      )}
                    </span>
                    {!isLast && <Fragment>&nbsp; </Fragment>}
                  </Fragment>
                )
              })}
            </Typography>
          </div>
          <div css={tw`h-px -mx-4 bg-[#EDEDED]`} />
          <div>
            <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`mb-2`}>
              Applied filters:
            </Typography>
            {hasFilters && (
              <div css={tw`flex flex-col mb-2`}>
                <div css={tw`h-px -mx-4 bg-[#EDEDED]`} />
                {filters.map((filter) => {
                  return (
                    <DataRow
                      key={filter.label}
                      title={filter.label}
                      text={filter.value}
                      className={tw`-mx-4 px-4`}
                    />
                  )
                })}
              </div>
            )}
            <WarningContainer
              titleVariant="subtitle1"
              title={
                hasFilters
                  ? `Export will include only ${entityNamePlural} matching these filters.`
                  : `No filters applied. All ${entityNamePlural} matching your export selection will be included.`
              }
            />
          </div>
          <div css={tw`h-px -mx-4 bg-[#EDEDED]`} />
          <div>
            <div css={tw`flex flex-row justify-between items-baseline mb-2`}>
              <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`max-w-[70%]`}>
                {`Number of ${entityNamePlural} to export`}
              </Typography>
              <div css={tw`flex flex-row items-baseline gap-1`}>
                <Typography variant="body2" color={colors.SHADES_GREY_400} fontWeight={500}>
                  Max:
                </Typography>
                <Typography variant="body2" color={colors.GRAY_BLACK} fontWeight={500}>
                  {maxCount.toLocaleString('en-US')}
                </Typography>
              </div>
            </div>
            <TextField
              fullWidth
              type="text"
              inputMode="numeric"
              value={count.toLocaleString('en-US')}
              disabled={isSubmitting}
              onChange={handleChangeCount}
              data-testid="export-data-modal-count-input"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      variant="subtitle1"
                      color={count >= maxCount ? colors.SHADES_GREY_400 : colors.PRIMARY_PURPLE_500}
                      css={[
                        tw`font-semibold`,
                        !isSubmitting &&
                          (count < maxCount ? tw`cursor-pointer` : tw`cursor-not-allowed`),
                      ]}
                      role="button"
                      tabIndex={isSubmitting ? -1 : 0}
                      onClick={isSubmitting ? undefined : handleClickMax}
                      onKeyDown={handleKeyDownMax}
                      data-testid="export-data-modal-max-btn"
                    >
                      Max
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>
        <div
          css={tw`flex flex-row justify-between items-center border-0 border-t border-solid border-[#EDEDED] p-4`}
        >
          <Button
            color="grey"
            size="small"
            disabled={isSubmitting}
            onClick={handleClose}
            data-testid="export-data-modal-close-btn"
          >
            Close
          </Button>
          <Button
            color="primary"
            size="small"
            loading={isSubmitting}
            disabled={isSubmitting || count <= 0}
            onClick={handleSubmit}
            data-testid="export-data-modal-submit-btn"
          >
            Export CSV
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const ExportDataModal = memo(ExportDataModalComponent)
